import { Component, ContentChild, EventEmitter, HostBinding, Input, ViewChild, Output, ElementRef, Renderer2, HostListener } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DialogActionsComponent } from './dialog-actions.component';
import { DialogTitleBarComponent } from './dialog-titlebar.component';
import { LocalizationService, L10N_PREFIX } from '@progress/kendo-angular-l10n';
import { hasClasses, Keys, isPresent, focusableSelector, isFocusable, DIALOG_ELEMENTS_HANDLING_ARROWS, DIALOG_ELEMENTS_HANDLING_ESC_KEY } from '../common/util';
/**
 * Represents the Kendo UI Dialog component for Angular.
 */
var DialogComponent = /** @class */ (function () {
    function DialogComponent(_elRef, _renderer, localization) {
        var _this = this;
        this._elRef = _elRef;
        this._renderer = _renderer;
        /**
         * @hidden
         */
        this.titleId = null;
        /**
         * Fires when the user clicks on the **Close** button of the Dialog.
         */
        this.action = new EventEmitter();
        /**
         * Fires when the user clicks on the **Close** button of the Dialog.
         */
        this.close = new EventEmitter();
        this.tabIndex = 0;
        this.subscriptions = [];
        this.direction = localization.rtl ? 'rtl' : 'ltr';
        this.subscriptions.push(localization.changes.subscribe(function (_a) {
            var rtl = _a.rtl;
            return _this.direction = rtl ? 'rtl' : 'ltr';
        }));
        this.titleId = this.generateTitleId();
    }
    Object.defineProperty(DialogComponent.prototype, "dir", {
        get: function () {
            return this.direction;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    DialogComponent.prototype.onComponentKeydown = function (event) {
        var target = event.target;
        var parent = target.parentElement;
        if (hasClasses(target, DIALOG_ELEMENTS_HANDLING_ESC_KEY) ||
            hasClasses(parent, DIALOG_ELEMENTS_HANDLING_ESC_KEY)) {
            if (event.keyCode === Keys.esc) {
                this.close.emit();
            }
        }
        if (hasClasses(target, 'k-button') &&
            hasClasses(parent, DIALOG_ELEMENTS_HANDLING_ARROWS) &&
            (event.keyCode === Keys.left || event.keyCode === Keys.right)) {
            this.handleActionButtonFocus(parent, event.keyCode);
        }
        if (event.keyCode === Keys.tab) {
            this.keepFocusWithinComponent(target, event);
        }
    };
    DialogComponent.prototype.ngAfterContentInit = function () {
        this.bubble('close', this.titlebarContent);
        if (this.titlebarContent) {
            this.titlebarContent.id = this.titleId;
        }
    };
    DialogComponent.prototype.ngAfterViewInit = function () {
        this.handleInitialFocus();
        this.bubble('close', this.titlebarView);
        this.bubble('action', this.actionsView);
        if (this.titlebarView || this.titlebarContent) {
            //Needed for Dialogs created via service
            this._renderer.setAttribute(this._elRef.nativeElement.querySelector('.k-dialog'), 'aria-labelledby', this.titleId);
        }
    };
    DialogComponent.prototype.ngOnInit = function () {
        this._renderer.removeAttribute(this._elRef.nativeElement, 'title');
    };
    DialogComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.forEach(function (s) { return s.unsubscribe(); });
        this.subscriptions = [];
    };
    /**
     * Focuses the wrapper of the Dialog component.
     */
    DialogComponent.prototype.focus = function () {
        var wrapper = this._elRef.nativeElement;
        if (isPresent(wrapper)) {
            wrapper.focus();
        }
    };
    /**
     * @hidden
     */
    DialogComponent.prototype.handleInitialFocus = function () {
        var wrapper = this._elRef.nativeElement;
        var primaryButton = wrapper.querySelector('.k-primary');
        if (this.shouldFocusPrimary(primaryButton)) {
            primaryButton.focus();
        }
        else {
            wrapper.focus();
        }
    };
    /**
     * @hidden
     */
    DialogComponent.prototype.handleActionButtonFocus = function (parent, key) {
        var focusableActionButtons = this.getAllFocusableChildren(parent);
        for (var i = 0; i < focusableActionButtons.length; i++) {
            var current = focusableActionButtons[i];
            if (current === document.activeElement) {
                if (key === Keys.left && i > 0) {
                    focusableActionButtons[i - 1].focus();
                    break;
                }
                if (key === Keys.right && i < focusableActionButtons.length - 1) {
                    focusableActionButtons[i + 1].focus();
                    break;
                }
            }
        }
    };
    /**
     * @hidden
     */
    DialogComponent.prototype.keepFocusWithinComponent = function (target, event) {
        var firstFocusable = this._elRef.nativeElement;
        var lastFocusable = this.getLastFocusableElement(firstFocusable);
        var tabBeforeFirstFocusable = (target === firstFocusable) && event.shiftKey;
        var tabAfterLastFocusable = !event.shiftKey && isPresent(lastFocusable) && (target === lastFocusable);
        var tabWithNoFocusable = !isPresent(lastFocusable) && !event.shiftKey;
        if (tabBeforeFirstFocusable || tabWithNoFocusable) {
            event.preventDefault();
            firstFocusable.focus();
        }
        if (tabAfterLastFocusable) {
            event.preventDefault();
            lastFocusable.focus();
        }
    };
    /**
     * @hidden
     */
    DialogComponent.prototype.shouldFocusPrimary = function (el) {
        return isPresent(el) && isFocusable(el);
    };
    /**
     * @hidden
     */
    DialogComponent.prototype.getAllFocusableChildren = function (parent) {
        return parent.querySelectorAll(focusableSelector);
    };
    /**
     * @hidden
     */
    DialogComponent.prototype.getLastFocusableElement = function (parent) {
        var all = this.getAllFocusableChildren(parent);
        return (all.length > 0) ? all[all.length - 1] : null;
    };
    /**
     * @hidden
     */
    DialogComponent.prototype.generateTitleId = function () {
        return "kendo-dialog-title-" + Math.ceil(Math.random() * 1000000).toString();
    };
    Object.defineProperty(DialogComponent.prototype, "wrapperClass", {
        get: function () {
            return true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DialogComponent.prototype, "styles", {
        get: function () {
            var styles = {};
            if (this.width) {
                styles.width = this.width + 'px';
            }
            if (this.height) {
                styles.height = this.height + 'px';
            }
            if (this.minWidth) {
                styles.minWidth = this.minWidth + 'px';
            }
            return styles;
        },
        enumerable: true,
        configurable: true
    });
    DialogComponent.prototype.bubble = function (eventName, component) {
        var _this = this;
        if (component) {
            var emit = function (e) { return _this[eventName].emit(e); };
            var s = component[eventName].subscribe(emit);
            this.subscriptions.push(s);
        }
    };
    DialogComponent.decorators = [
        { type: Component, args: [{
                    animations: [
                        trigger('overlayAppear', [
                            state('in', style({ opacity: 1 })),
                            transition('void => *', [
                                style({ opacity: .1 }),
                                animate('.3s cubic-bezier(.2, .6, .4, 1)')
                            ])
                        ]),
                        trigger('dialogSlideInAppear', [
                            state('in', style({ transform: 'translate(0, 0)' })),
                            transition('void => *', [
                                style({ transform: 'translate(0, -10%)' }),
                                animate('.3s cubic-bezier(.2, 1, .2, 1)')
                            ])
                        ])
                    ],
                    exportAs: 'kendoDialog',
                    providers: [
                        LocalizationService,
                        {
                            provide: L10N_PREFIX,
                            useValue: 'kendo.dialog'
                        }
                    ],
                    selector: 'kendo-dialog',
                    template: "\n    <div class=\"k-overlay\" @overlayAppear></div>\n\n    <div\n      class=\"k-widget k-window k-dialog\"\n      role=\"dialog\"\n      [ngStyle]=\"styles\"\n      @dialogSlideInAppear\n    >\n      <kendo-dialog-titlebar *ngIf=\"title\" [id]=\"titleId\">{{title}}</kendo-dialog-titlebar>\n      <ng-content select=\"kendo-dialog-titlebar\" *ngIf=\"!title\"></ng-content>\n\n      <div class=\"k-content k-window-content k-dialog-content\">\n        <ng-content *ngIf=\"!contentTemplate\"></ng-content>\n        <ng-template [ngTemplateOutlet]=\"contentTemplate\" *ngIf=\"contentTemplate\"></ng-template>\n      </div>\n\n      <ng-content select=\"kendo-dialog-actions\" *ngIf=\"!actions\"></ng-content>\n      <kendo-dialog-actions [actions]=\"actions\" *ngIf=\"actions\"></kendo-dialog-actions>\n\n    </div>\n  "
                },] },
    ];
    /** @nocollapse */
    DialogComponent.ctorParameters = function () { return [
        { type: ElementRef, },
        { type: Renderer2, },
        { type: LocalizationService, },
    ]; };
    DialogComponent.propDecorators = {
        'title': [{ type: Input },],
        'actions': [{ type: Input },],
        'width': [{ type: Input },],
        'minWidth': [{ type: Input },],
        'height': [{ type: Input },],
        'action': [{ type: Output },],
        'close': [{ type: Output },],
        'dir': [{ type: HostBinding, args: ['attr.dir',] },],
        'tabIndex': [{ type: HostBinding, args: ['attr.tabIndex',] },],
        'titlebarContent': [{ type: ContentChild, args: [DialogTitleBarComponent,] },],
        'titlebarView': [{ type: ViewChild, args: [DialogTitleBarComponent,] },],
        'actionsView': [{ type: ViewChild, args: [DialogActionsComponent,] },],
        'onComponentKeydown': [{ type: HostListener, args: ['keydown', ['$event'],] },],
        'wrapperClass': [{ type: HostBinding, args: ['class.k-dialog-wrapper',] },],
    };
    return DialogComponent;
}());
export { DialogComponent };



//////////////////
// WEBPACK FOOTER
// C:/spro/Workspaces/VisualCode/Ecoltex2/node_modules/@progress/kendo-angular-dialog/dist/es/dialog/dialog.component.js
// module id = ../../../../@progress/kendo-angular-dialog/dist/es/dialog/dialog.component.js
// module chunks = vendor